import type { StudioOption } from '../types';

/**
 * 배경(스튜디오 환경) 프리셋.
 *
 * 각 프리셋은 배경색·바닥·조명·환경맵을 한 세트로 묶습니다. 배경색만 바꾸면
 * 차체 반사와 그림자가 배경과 어긋나서 합성한 것처럼 보이기 때문에, 반사에 쓰이는
 * Lightformer 색과 접지 그림자 농도까지 같이 정의합니다.
 *
 * 배경은 차량 사양이 아니라 뷰어 설정이므로 가격에 영향을 주지 않습니다.
 *
 * `lightformers`는 @react-three/drei의 <Lightformer>에 그대로 펼쳐 넣는 props입니다.
 * 이 발광 판들이 환경맵으로 구워져서 차체의 클리어코트 반사를 만듭니다.
 */
export const studioOptions: StudioOption[] = [
  {
    id: 'dark-studio',
    name: '다크 스튜디오',
    description: '무광 검정 호리존트',
    backgroundColor: '#14161b',
    floorColor: '#1b1e25',
    floorRoughness: 0.85,
    floorMetalness: 0.05,
    ambientIntensity: 0.25,
    keyLight: { position: [6, 8, 4], intensity: 1.2 },
    fillLight: { position: [-6, 4, -6], intensity: 0.4 },
    // 어두운 배경에서는 차체 실루엣이 그림자에 묻히지 않도록 농도를 높게 잡습니다.
    contactShadowOpacity: 0.7,
    lightformers: [
      { form: 'rect', intensity: 6, position: [0, 6, 0], rotation: [Math.PI / 2, 0, 0], scale: [10, 4, 1] },
      { form: 'rect', intensity: 2, color: '#dfe8ff', position: [0, 2, -8], scale: [12, 3, 1] },
      { form: 'rect', intensity: 2, color: '#ffe9d6', position: [0, 2, 8], rotation: [0, Math.PI, 0], scale: [12, 3, 1] },
      { form: 'ring', intensity: 3, position: [8, 3, 0], rotation: [0, -Math.PI / 2, 0], scale: 3 },
    ],
  },

  {
    id: 'white-studio',
    name: '화이트 스튜디오',
    description: '무한 백색 배경',
    backgroundColor: '#eceff3',
    floorColor: '#d5dae1',
    floorRoughness: 0.6,
    floorMetalness: 0.05,
    // 밝은 배경에서는 ambient를 올려야 차체 아랫면이 검게 뭉치지 않습니다.
    ambientIntensity: 0.75,
    keyLight: { position: [5, 9, 5], intensity: 1.5 },
    fillLight: { position: [-6, 5, -5], intensity: 0.8 },
    // 배경이 밝으면 진한 그림자가 오려붙인 것처럼 보여서 옅게 깔아 줍니다.
    contactShadowOpacity: 0.32,
    lightformers: [
      { form: 'rect', intensity: 8, position: [0, 7, 0], rotation: [Math.PI / 2, 0, 0], scale: [14, 8, 1] },
      { form: 'rect', intensity: 4, color: '#ffffff', position: [0, 3, -9], scale: [16, 6, 1] },
      { form: 'rect', intensity: 4, color: '#ffffff', position: [0, 3, 9], rotation: [0, Math.PI, 0], scale: [16, 6, 1] },
      { form: 'rect', intensity: 3, color: '#ffffff', position: [-9, 3, 0], rotation: [0, Math.PI / 2, 0], scale: [12, 6, 1] },
      { form: 'rect', intensity: 3, color: '#ffffff', position: [9, 3, 0], rotation: [0, -Math.PI / 2, 0], scale: [12, 6, 1] },
    ],
  },

  {
    id: 'sunset-loft',
    name: '선셋 로프트',
    description: '노을이 드는 콘크리트 로프트',
    backgroundColor: '#2b1d17',
    floorColor: '#3a2a22',
    floorRoughness: 0.7,
    floorMetalness: 0.1,
    ambientIntensity: 0.35,
    keyLight: { position: [-7, 4, 6], intensity: 1.6 },
    fillLight: { position: [6, 5, -5], intensity: 0.35 },
    contactShadowOpacity: 0.6,
    lightformers: [
      // 낮게 깔린 큰 주황 판이 노을 역할을 합니다. 차체 측면에 긴 하이라이트가 생깁니다.
      { form: 'rect', intensity: 7, color: '#ff9d4d', position: [-9, 2.2, 4], rotation: [0, Math.PI / 2, 0], scale: [14, 5, 1] },
      { form: 'rect', intensity: 3, color: '#ffd7a8', position: [0, 6, 0], rotation: [Math.PI / 2, 0, 0], scale: [10, 5, 1] },
      { form: 'rect', intensity: 1.6, color: '#5b6f9c', position: [7, 3, -6], scale: [10, 4, 1] },
      { form: 'ring', intensity: 4, color: '#ffb066', position: [-6, 1.4, -4], scale: 2.4 },
    ],
  },

  {
    id: 'forest-slope',
    name: '포레스트 슬로프',
    description: '소나무 숲길의 부드러운 자연광',
    // HDRI가 로드되기 전에도 숲과 비슷한 색감으로 자연스럽게 이어집니다.
    backgroundColor: '#7f8c72',
    floorColor: '#53584a',
    floorRoughness: 0.96,
    floorMetalness: 0,
    // HDRI 자체가 간접광을 제공하므로 기존 스튜디오보다 보조광을 낮게 둡니다.
    ambientIntensity: 0.22,
    keyLight: { position: [-6, 8, 5], intensity: 0.55 },
    fillLight: { position: [7, 4, -5], intensity: 0.18 },
    contactShadowOpacity: 0.48,
    environment: {
      files: '/environments/forest-slope-1k.hdr',
      // 지면 투영과 배경이 같은 원본 픽셀을 쓰도록 밝기·블러·회전을 중립값에 둡니다.
      backgroundBlurriness: 0,
      backgroundIntensity: 1,
      environmentIntensity: 0.85,
      rotation: [0, 0, 0],
      // Forest Slope의 촬영 눈높이에 맞춰 하단 HDR 픽셀을 평면 지면처럼 투영합니다.
      // scale은 카메라 far(120) 안에 있어야 투영 구가 클리핑되지 않습니다.
      ground: { height: 1.65, radius: 35, scale: 100 },
    },
    // 사진 환경을 읽는 동안 사용할 오프라인 procedural fallback입니다.
    lightformers: [
      { form: 'rect', intensity: 2.8, color: '#edf4dc', position: [0, 7, 0], rotation: [Math.PI / 2, 0, 0], scale: [12, 6, 1] },
      { form: 'rect', intensity: 1.8, color: '#ffd9a8', position: [-8, 3, 5], rotation: [0, Math.PI / 2, 0], scale: [10, 4, 1] },
      { form: 'rect', intensity: 1.2, color: '#a9bea0', position: [7, 3, -6], scale: [10, 4, 1] },
    ],
  },

  {
    id: 'fish-hoek-beach',
    name: '피시 후크 비치',
    description: '흐린 일출의 고요한 모래 해변',
    // HDRI 로딩 fallback도 모래와 흐린 하늘의 저대비 색감으로 맞춥니다.
    backgroundColor: '#a8b5c5',
    floorColor: '#b9b0a0',
    floorRoughness: 0.96,
    floorMetalness: 0,
    // 흐린 해변의 확산광을 살리고 별도 직사광은 약하게 보충합니다.
    ambientIntensity: 0.32,
    keyLight: { position: [-6, 5, 4], intensity: 0.35 },
    fillLight: { position: [5, 4, -5], intensity: 0.22 },
    contactShadowOpacity: 0.36,
    environment: {
      files: '/environments/fish-hoek-beach-2k.hdr',
      backgroundBlurriness: 0,
      backgroundIntensity: 1,
      environmentIntensity: 0.9,
      // GroundProjectedEnv는 지면 텍스처 회전을 별도로 지원하지 않으므로 중립 회전을 씁니다.
      rotation: [0, 0, 0],
      // 열린 해변은 먼 수평선을 유지하도록 숲보다 큰 투영 반경을 사용합니다.
      ground: { height: 1.55, radius: 80, scale: 100 },
    },
    lightformers: [
      { form: 'rect', intensity: 2.5, color: '#dbe7f4', position: [0, 7, 0], rotation: [Math.PI / 2, 0, 0], scale: [14, 8, 1] },
      { form: 'rect', intensity: 1.5, color: '#ffdcb2', position: [-8, 2.5, 4], rotation: [0, Math.PI / 2, 0], scale: [12, 4, 1] },
      { form: 'rect', intensity: 1.2, color: '#9ec4dc', position: [7, 3, -6], scale: [12, 5, 1] },
    ],
  },

  {
    id: 'zwartkops-circuit',
    name: '즈워트콥스 서킷',
    description: '맑은 오후의 스타팅 그리드',
    backgroundColor: '#5e9dcc',
    floorColor: '#30343a',
    floorRoughness: 0.74,
    floorMetalness: 0.04,
    // HDRI의 강한 태양광을 주광으로 쓰고 하부가 뭉치지 않을 만큼만 보조합니다.
    ambientIntensity: 0.18,
    keyLight: { position: [-5, 10, 3], intensity: 0.5 },
    fillLight: { position: [6, 4, -5], intensity: 0.16 },
    contactShadowOpacity: 0.52,
    environment: {
      files: '/environments/zwartkops-start-afternoon-2k.hdr',
      backgroundBlurriness: 0,
      backgroundIntensity: 1,
      environmentIntensity: 0.9,
      rotation: [0, 0, 0],
      // 서킷 노면과 그리드 라인이 차량 아래에서 평면으로 이어지도록 투영합니다.
      ground: { height: 1.55, radius: 80, scale: 100 },
    },
    lightformers: [
      { form: 'rect', intensity: 4, color: '#e8f1ff', position: [0, 8, 0], rotation: [Math.PI / 2, 0, 0], scale: [14, 8, 1] },
      { form: 'ring', intensity: 4.5, color: '#fff0cf', position: [-8, 7, 2], rotation: [0, Math.PI / 2, 0], scale: 2.5 },
      { form: 'rect', intensity: 1.4, color: '#b7d6ef', position: [7, 3, -6], scale: [12, 4, 1] },
    ],
  },

  {
    id: 'neon-night',
    name: '네온 나이트',
    description: '시안·마젠타 림라이트',
    backgroundColor: '#080c16',
    floorColor: '#0d1424',
    // 젖은 아스팔트처럼 바닥에 네온이 비치게 낮은 roughness를 씁니다.
    floorRoughness: 0.28,
    floorMetalness: 0.35,
    ambientIntensity: 0.18,
    keyLight: { position: [4, 7, 5], intensity: 0.7 },
    fillLight: { position: [-5, 3, -6], intensity: 0.5 },
    contactShadowOpacity: 0.75,
    lightformers: [
      { form: 'rect', intensity: 5, color: '#3ff0ff', position: [0, 2.4, -7], scale: [14, 1.2, 1] },
      { form: 'rect', intensity: 5, color: '#ff4fd8', position: [0, 2.4, 7], rotation: [0, Math.PI, 0], scale: [14, 1.2, 1] },
      { form: 'rect', intensity: 2.4, color: '#8fd8ff', position: [0, 6.5, 0], rotation: [Math.PI / 2, 0, 0], scale: [8, 3, 1] },
      { form: 'ring', intensity: 6, color: '#3ff0ff', position: [-8, 2, 2], rotation: [0, Math.PI / 2, 0], scale: 2 },
      { form: 'ring', intensity: 6, color: '#ff4fd8', position: [8, 2, -2], rotation: [0, -Math.PI / 2, 0], scale: 2 },
    ],
  },
];

/**
 * 첫 화면의 배경.
 *
 * 시안·마젠타 림라이트가 차체 어깨선을 가장 선명하게 긋습니다. 다크 스튜디오는 사진
 * 촬영용으로는 정석이지만 첫인상에서는 차와 배경이 같이 어두워서 실루엣만 남습니다.
 *
 * 배열 첫 항목(studioOptions[0].id)이 아니라 id를 직접 씁니다. 스와치 순서는 화면
 * 배치의 문제이고 기본 배경은 첫인상의 문제인데, 둘을 한 값으로 묶어 두면 순서를
 * 손볼 때 기본 배경이 조용히 따라 바뀝니다. 없는 id를 적으면 배경 섹션이 렌더에서
 * 터지므로 App.test.tsx가 바로 잡습니다.
 */
export const DEFAULT_STUDIO_ID = 'neon-night';

/**
 * 주행 모드에 들어갈 때 갈아 끼우는 배경.
 *
 * 주행은 바닥을 아스팔트 트랙으로 바꿉니다(DrivingGround). 스튜디오 프리셋은 그 위가
 * 단색 호리존트라 트랙이 허공에 뜬 판처럼 보이고, 지나가는 배경이 없어서 속도도
 * 읽히지 않습니다. 서킷 HDRI는 하늘과 먼 풍경을 채워서 노면이 어디로 이어지는지
 * 보이게 합니다.
 *
 * 나올 때 되돌리지는 않습니다. 주행 중에도 배경 스와치는 잠기지 않으므로
 * (OptionPanel의 specLocked 주석) 몰면서 고른 배경이 있을 수 있는데, 되돌리면 그
 * 선택을 모드 전환이 말없이 덮어씁니다.
 */
export const DRIVING_STUDIO_ID = 'zwartkops-circuit';

export function findStudioById(studioId: string): StudioOption | undefined {
  return studioOptions.find((studio) => studio.id === studioId);
}
